/**
 * Candidate-facing translations (see ./config.js for which journeys are not English).
 *
 * The English sentence is the key: t('Start interview') returns the French text when the journey is
 * French and the dictionary has it, and the English text otherwise. English candidates are therefore
 * never affected, and an untranslated sentence falls back to English instead of showing a key.
 *
 *   const t = useCandidateLanguage({ screeningId, assessmentId, interviewId }); // page level
 *   const t = useCandidateT();                                                  // shared components
 *   t('Question {current} of {total}', { current: 2, total: 10 })
 *
 * Server error messages can be passed straight through t() — a message present in the dictionary is
 * translated, any other is shown as sent.
 */
import { useEffect, useMemo, useSyncExternalStore } from 'react';
import { CANDIDATE_LANGUAGE_JOURNEYS } from './config';
import fr from './fr';

const DICTIONARIES = { fr };

// The language lives outside React so shared components (modals, proctoring, the agent control bar)
// follow the page without prop drilling. It is in memory only: a reload starts in English, and every
// page that knows its screening / assessment / interview id decides the language again, so nothing
// French can carry over into another journey.
let current = 'en';
const listeners = new Set();

function setCandidateLanguage(lang) {
  if (lang === current) return;
  current = lang;
  if (typeof document !== 'undefined') document.documentElement.lang = lang;
  listeners.forEach((fn) => fn());
}

const subscribe = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};
const getSnapshot = () => current;

/**
 * 'fr' / 'en' when any known id decides it, or null when none of the ids is known yet (still loading)
 * — the caller then keeps whatever language is already active.
 */
export function resolveCandidateLanguage({ screeningId, assessmentId, interviewId } = {}) {
  const ids = { screeningId, assessmentId, interviewId };
  const known = Object.entries(ids).filter(([, v]) => v);
  if (!known.length) return null;
  const match = CANDIDATE_LANGUAGE_JOURNEYS.find((j) => known.some(([k, v]) => j[k] === String(v)));
  return match ? match.language : 'en';
}

export function translate(lang, text, vars) {
  if (typeof text !== 'string') return text;
  const out = (lang !== 'en' && DICTIONARIES[lang]?.[text]) || text;
  if (!vars) return out;
  // A placeholder given as null/undefined renders empty, as `${value}` in JSX used to.
  return out.replace(/\{(\w+)\}/g, (m, k) => {
    if (!(k in vars)) return m;
    return vars[k] === undefined || vars[k] === null ? '' : String(vars[k]);
  });
}

/** Translator for shared components: follows whatever language the current page set. */
export function useCandidateT() {
  const lang = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return useMemo(() => {
    const t = (text, vars) => translate(lang, text, vars);
    t.lang = lang;
    return t;
  }, [lang]);
}

/**
 * Page-level hook: decides the journey's language from whichever ids the page knows, makes it the
 * active language for shared components, and returns the translator.
 */
export function useCandidateLanguage({ screeningId, assessmentId, interviewId } = {}) {
  const resolved = resolveCandidateLanguage({ screeningId, assessmentId, interviewId });
  const active = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const lang = resolved || active;

  useEffect(() => {
    if (resolved) setCandidateLanguage(resolved);
  }, [resolved]);

  return useMemo(() => {
    const t = (text, vars) => translate(lang, text, vars);
    t.lang = lang;
    return t;
  }, [lang]);
}
